import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import { Camera, CheckCircle2, Clock, IdCard, ScrollText, Building2, ShieldCheck, XCircle } from 'lucide-react'
import { Page, PageHeader, Card, CardHeader, btnPrimary, SkeletonRows } from '@/components/rm'
import { Field, Input, Select } from '@/components/rm/form'
import { useAuth } from '@/hooks/use-auth'
import { fmtDate, manilaDayIso, phDay } from '@/lib/ph'
import { ID_TYPES, idTypeLabel, useMyChecks, useSendCheck, type Check, type CheckKind } from '@/lib/verification'
import { cn, errorText } from '@/lib/utils'

// Spec 010. The business's checks: ID (required to be found and paid), business registration and
// licences (optional badges). A business tool, so English. Only the owner or an admin sends.
export const Route = createFileRoute('/_app/verification')({ component: VerificationPage })

function VerificationPage() {
  const { role } = useAuth()
  const canSend = role === 'company-admin'
  const q = useMyChecks()
  if (q.isLoading) return <Page><PageHeader title="Verification" /><Card className="mt-5"><SkeletonRows /></Card></Page>
  const checks = q.data ?? []
  const latest = (k: CheckKind) => checks.find((c) => c.kind === k && c.status !== 'uploading')
  const id = latest('id')
  const biz = latest('business')
  const licences = checks.filter((c) => c.kind === 'licence' && c.status !== 'uploading')
  const idOk = checks.some((c) => c.kind === 'id' && c.status === 'approved')

  return (
    <Page>
      <PageHeader title="Verification" subtitle="Clients let you into their home. These checks show them who you are." />
      {!idOk && (
        <div className="mt-5 flex items-start gap-3 rounded-[14px] border border-token-warning bg-token-warning-surface p-4">
          <ShieldCheck className="mt-0.5 h-5 w-5 flex-none text-token-warning" />
          <div className="text-[13.5px] text-token-primary">
            <p className="font-semibold">{id?.status === 'pending' ? 'Your ID is being checked.' : 'Clients cannot find, book or pay you until your ID is checked.'}</p>
            <p className="text-token-secondary">{id?.status === 'pending' ? 'This usually takes one business day. You get a notice when it is done.' : 'Send a photo of a government ID and a selfie. It takes about 3 minutes.'}</p>
          </div>
        </div>
      )}
      {!canSend && <p className="mt-4 text-[13px] text-token-secondary">Only the business owner or an admin can send checks. You can see their status here.</p>}

      <Card className="mt-5">
        <CardHeader title="ID check (required)" />
        <div className="space-y-4 px-5 pb-5">
          <Status c={id} approvedText={id ? `${idTypeLabel(id.id_type)} ending ${id.number_last4}` : ''} />
          {canSend && !idOk && id?.status !== 'pending' && <IdForm />}
        </div>
      </Card>

      <Card className="mt-5">
        <CardHeader title="Business registration (optional)" />
        <div className="space-y-4 px-5 pb-5">
          <Status c={biz} approvedText={biz ? `${biz.reg_kind?.toUpperCase()} ${biz.reg_number}${biz.business_name ? ` · ${biz.business_name}` : ''}` : ''} />
          {canSend && biz?.status !== 'approved' && biz?.status !== 'pending' && <BusinessForm />}
        </div>
      </Card>

      <Card className="mt-5">
        <CardHeader title="Licences (optional)" />
        <div className="space-y-4 px-5 pb-5">
          <p className="text-[13px] text-token-secondary">For a regulated trade, for example a PRC registered electrician. Clients see each licence with its expiry date.</p>
          {licences.map((l) => (
            <Status key={l.id} c={l} approvedText={`${l.licence_trade || 'Licence'} · ${l.licence_body} ${l.licence_number}`}
              extra={l.licence_expires ? (l.licence_expires < manilaDayIso() ? `Expired ${fmtDate(phDay(l.licence_expires), 'medium')}. Add the new licence.` : `Valid until ${fmtDate(phDay(l.licence_expires), 'medium')}`) : undefined} />
          ))}
          {canSend && !licences.some((l) => l.status === 'pending') && <LicenceForm />}
        </div>
      </Card>
    </Page>
  )
}

function Status({ c, approvedText, extra }: { c: Check | undefined; approvedText: string; extra?: string }) {
  if (!c) return <p className="text-[13.5px] text-token-secondary">Not sent yet.</p>
  const row = (icon: ReactNode, title: string, body?: string | null, tone = 'text-token-primary') => (
    <div className="flex items-start gap-2.5">
      {icon}
      <div className="text-[13.5px]"><p className={cn('font-semibold', tone)}>{title}</p>{body && <p className="text-token-secondary">{body}</p>}{extra && <p className="text-token-secondary">{extra}</p>}</div>
    </div>
  )
  if (c.status === 'approved') return row(<CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-token-success" />, `Verified on ${fmtDate(c.decided_at!, 'medium')}`, approvedText, 'text-token-success')
  if (c.status === 'pending') return row(<Clock className="mt-0.5 h-5 w-5 flex-none text-token-warning" />, `Being checked since ${fmtDate(c.submitted_at!, 'medium')}`, 'Usually within one business day.')
  return row(<XCircle className="mt-0.5 h-5 w-5 flex-none text-token-danger" />, 'Not approved', `${c.reason ?? ''} Send it again below.`, 'text-token-danger')
}

/** A photo input that opens the camera on a phone. */
function PhotoInput({ label, hint, facing, file, onFile }: { label: string; hint: string; facing: 'user' | 'environment'; file: File | null; onFile: (f: File | null) => void }) {
  const url = useMemo(() => (file ? URL.createObjectURL(file) : null), [file])
  useEffect(() => () => { if (url) URL.revokeObjectURL(url) }, [url])
  return (
    <label className="block cursor-pointer">
      <span className="mb-1.5 block text-[13px] font-semibold text-token-secondary">{label}</span>
      <span className={cn('flex items-center gap-3 rounded-[12px] border border-dashed p-3', file ? 'border-token-success' : 'border-token-strong')}>
        {url ? <img src={url} alt="" className="h-16 w-24 flex-none rounded-[8px] object-cover" /> : <span className="flex h-16 w-24 flex-none items-center justify-center rounded-[8px] bg-token-hover"><Camera className="h-6 w-6 text-token-muted" /></span>}
        <span className="text-[13px] text-token-secondary">{file ? 'Tap to take it again' : hint}</span>
      </span>
      <input type="file" accept="image/*" capture={facing} className="sr-only" onChange={(e) => onFile(e.target.files?.[0] ?? null)} />
    </label>
  )
}

function useSend(kind: CheckKind, done: string) {
  const send = useSendCheck()
  const run = async (details: Record<string, string>, photos: Array<{ name: string; file: File }>) => {
    try { await send.mutateAsync({ kind, details, photos }); toast.success(done); return true } catch (e) { toast.error(errorText(e, 'Could not send it')); return false }
  }
  return { run, busy: send.isPending }
}

function IdForm() {
  const [idType, setIdType] = useState('')
  const [number, setNumber] = useState('')
  const [idPhoto, setIdPhoto] = useState<File | null>(null)
  const [selfie, setSelfie] = useState<File | null>(null)
  const { run, busy } = useSend('id', 'Sent. We check it within one business day.')
  const ok = idType && number.replace(/[^A-Za-z0-9]/g, '').length >= 4 && idPhoto && selfie
  return (
    <div className="space-y-4 rounded-[12px] bg-token-hover p-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="ID type">
          <Select value={idType} onChange={(e) => setIdType(e.target.value)}>
            <option value="">Choose</option>
            {ID_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </Select>
        </Field>
        <Field label="ID number"><Input value={number} onChange={(e) => setNumber(e.target.value)} autoComplete="off" /></Field>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <PhotoInput label="Photo of the ID" hint="The front, flat, all four corners in the photo, no glare" facing="environment" file={idPhoto} onFile={setIdPhoto} />
        <PhotoInput label="Selfie" hint="Your face, in good light, with nothing covering it" facing="user" file={selfie} onFile={setSelfie} />
      </div>
      <p className="flex items-start gap-2 text-[12.5px] text-token-secondary"><IdCard className="mt-0.5 h-4 w-4 flex-none" />Only MySerbisyo staff see these photos, and they are deleted when the check is done. We keep the ID type and the last 4 characters of the number.</p>
      <div className="flex justify-end">
        <button type="button" className={btnPrimary} disabled={!ok || busy} onClick={() => run({ id_type: idType, number }, [{ name: 'id', file: idPhoto! }, { name: 'selfie', file: selfie! }])}>{busy ? 'Sending' : 'Send for checking'}</button>
      </div>
    </div>
  )
}

function BusinessForm() {
  const [regKind, setRegKind] = useState<'dti' | 'sec'>('dti')
  const [number, setNumber] = useState('')
  const [name, setName] = useState('')
  const [tin, setTin] = useState('')
  const [photo, setPhoto] = useState<File | null>(null)
  const { run, busy } = useSend('business', 'Sent for checking.')
  const ok = number.replace(/[^A-Za-z0-9]/g, '').length >= 4 && name.trim() && photo
  return (
    <div className="space-y-4 rounded-[12px] bg-token-hover p-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Registered with"><Select value={regKind} onChange={(e) => setRegKind(e.target.value as 'dti' | 'sec')}><option value="dti">DTI (sole proprietor)</option><option value="sec">SEC (corporation or partnership)</option></Select></Field>
        <Field label="Registration number"><Input value={number} onChange={(e) => setNumber(e.target.value)} /></Field>
        <Field label="Registered business name"><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <Field label="TIN"><Input value={tin} onChange={(e) => setTin(e.target.value)} inputMode="numeric" placeholder="000-000-000-000" /></Field>
      </div>
      <PhotoInput label="Photo of the certificate" hint="The whole certificate, readable" facing="environment" file={photo} onFile={setPhoto} />
      <p className="flex items-start gap-2 text-[12.5px] text-token-secondary"><Building2 className="mt-0.5 h-4 w-4 flex-none" />The photo is deleted when the check is done. Clients see "Registered business", not the numbers.</p>
      <div className="flex justify-end"><button type="button" className={btnPrimary} disabled={!ok || busy} onClick={() => run({ reg_kind: regKind, number, business_name: name, tin }, [{ name: 'certificate', file: photo! }])}>{busy ? 'Sending' : 'Send for checking'}</button></div>
    </div>
  )
}

function LicenceForm() {
  const [open, setOpen] = useState(false)
  const [body, setBody] = useState('')
  const [trade, setTrade] = useState('')
  const [number, setNumber] = useState('')
  const [expires, setExpires] = useState('')
  const [photo, setPhoto] = useState<File | null>(null)
  const { run, busy } = useSend('licence', 'Sent for checking.')
  if (!open) return <button type="button" className="text-[13.5px] font-semibold text-brand hover:underline" onClick={() => setOpen(true)}>Add a licence</button>
  const ok = body.trim().length >= 2 && number.trim().length >= 3 && expires > manilaDayIso() && photo
  return (
    <div className="space-y-4 rounded-[12px] bg-token-hover p-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Issued by"><Input value={body} onChange={(e) => setBody(e.target.value)} placeholder="PRC, TESDA" /></Field>
        <Field label="Licence or trade"><Input value={trade} onChange={(e) => setTrade(e.target.value)} placeholder="Registered Master Electrician" /></Field>
        <Field label="Licence number"><Input value={number} onChange={(e) => setNumber(e.target.value)} /></Field>
        <Field label="Valid until"><Input type="date" value={expires} min={manilaDayIso()} onChange={(e) => setExpires(e.target.value)} /></Field>
      </div>
      <PhotoInput label="Photo of the licence" hint="The side with the number and the expiry date" facing="environment" file={photo} onFile={setPhoto} />
      <p className="flex items-start gap-2 text-[12.5px] text-token-secondary"><ScrollText className="mt-0.5 h-4 w-4 flex-none" />Clients see the licence and its expiry date. When it expires, the badge goes away until you add the new one.</p>
      <div className="flex justify-end">
        <button type="button" className={btnPrimary} disabled={!ok || busy} onClick={async () => {
          if (await run({ licence_body: body, licence_trade: trade, number, licence_expires: expires }, [{ name: 'licence', file: photo! }])) setOpen(false)
        }}>{busy ? 'Sending' : 'Send for checking'}</button>
      </div>
    </div>
  )
}
